import { createRouter, createWebHistory } from 'vue-router';

import Home from '../views/PublicHomeView.vue'
import AdminDb from '@/views/AdminView.vue';
import ResetPassword from '@/views/ResetPasswordView.vue'
import LegalDocument from '@/views/LegalDocumentView.vue'

import WatchDashboard from '@/views/WatchDashboard.vue'
import CompanyDetail from '@/views/watch/CompanyDetail.vue'
import SeedDetail from '@/views/watch/SeedDetail.vue'

import { getAuth, onAuthStateChanged, reload, multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import { logOut } from "@/firebase/auth";
import { checkAdminId } from "@/firebase/firestore";

const routes = [
  {
    path: '/attorney-signup',
    name: 'Attorney-Signup',
    component: () => import('@/views/AttorneySignupView.vue'),
  },
  {
    path: '/admin-signin',
    name: 'Admin-Signin',
    component: () => import('@/views/HomeView.vue'),
  },
  {
    path: '/admin-profile',
    name: 'Admin-Profile',
    component: () => import('@/views/AdminProfileView.vue'),
    meta: { requiresAuth: true, adminOnly: true },
  },
  {
    path: '/admin-security',
    name: 'Admin-Security',
    component: () => import('@/views/AdminSecurityView.vue'),
    meta: { requiresAuth: true, adminOnly: true },
  },
  {
    path: '/',
    name: 'LLFC-Home',
    component: Home,
  },
  {
    path: '/admin-db',
    name: 'Admin-Db',
    component: AdminDb,
    meta: { requiresAuth: true, adminOnly: true },
  },
  {
    path: '/reset-password',
    name: 'Reset-Password',
    component: ResetPassword,
  },
  {
    path: '/terms',
    name: 'Legal-Terms',
    component: LegalDocument,
    props: {
      documentId: 'app-terms-conditions',
      pageTitle: 'App Terms & Conditions'
    },
  },
  {
    path: '/privacy',
    name: 'Legal-Privacy',
    component: LegalDocument,
    props: {
      documentId: 'privacy-policy',
      pageTitle: 'Privacy Policy'
    },
  },
  {
    path: '/app-contract',
    name: 'Legal-App-Contract',
    component: LegalDocument,
    props: {
      documentId: 'example-app-contract',
      pageTitle: 'Limited Scope Agreement'
    },
  },
  {
    path: '/ai-confidentiality',
    name: 'Legal-AI-Confidentiality',
    component: LegalDocument,
    props: {
      documentId: 'ai-confidentiality',
      pageTitle: 'AI & Confidentiality'
    },
  },
  {
    path: '/watch',
    name: 'Watch-Dashboard',
    component: WatchDashboard,
    meta: { requiresAuth: true, adminOnly: true },
  },
  {
    path: '/watch/company/:companyId',
    name: 'Watch-Company',
    component: CompanyDetail,
    props: true,
    meta: { requiresAuth: true, adminOnly: true },
  },
  {
    path: '/watch/seed/:id',
    name: 'Watch-Seed',
    component: SeedDetail,
    props: true,
    meta: { requiresAuth: true, adminOnly: true },
  },
  
  // (Optional) Admin-only mirror route
  {
    path: '/admin/watch',
    name: 'Admin-Watch',
    component: WatchDashboard,
    meta: { requiresAuth: true, adminOnly: true },
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});


router.beforeEach(async (to, from, next) => {
  if (window.location.hostname === "legal.askmaix.com" && to.path === "/") {
    return next("/terms");
  }

  const auth = getAuth();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const metaAdminOnly = to.matched.some((record) => record.meta && record.meta.adminOnly);
  const isAdminDbRoute = to.path === "/admin-db"; // keep legacy protection
  const isAdminRoute = metaAdminOnly || isAdminDbRoute;

  if (!requiresAuth && !isAdminRoute) return next();

  try {
    const user = await new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (u) => {
        unsubscribe();
        resolve(u);
      });
    });

    if (!user) {
      console.warn("🚨 User not authenticated. Redirecting to home...");
      return next("/");
    }

    if (isAdminRoute) {
      const email = String(user.email || "").trim().toLowerCase();
      const approved = email.endsWith("@thelomaxlawfirm.com") && await checkAdminId(user.uid);

      if (!approved || auth.currentUser?.uid !== user.uid) {
        alert("Access denied. An approved staff account is required.");
        await logOut();
        return next("/");
      }

      // Fetch enrollment from Firebase before mounting any protected workspace.
      // The setup route still requires the same approved staff identity above.
      if (to.path !== "/admin-security") {
        await reload(user);
        if (auth.currentUser?.uid !== user.uid) return next("/");
        const hasAuthenticator = multiFactor(user).enrolledFactors.some(factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);
        if (!hasAuthenticator) return next("/admin-security");
      }
    }

    return next();
  } catch (error) {
    console.error("❌ Error in auth check:", error);
    if (isAdminRoute) {
      alert("Unable to verify staff access. Please sign in again when connected.");
      try { await logOut(); } catch (signOutError) { console.error("Unable to sign out:", signOutError); }
    }
    return next("/");
  }
});



export default router;
